'use client';

import { useState } from 'react';

import { Icon } from '@/components/ui/Icon/Icon';
import { AnimatePresence, motion } from '@/libs';
import { ticketLabels } from '@/utils';

import { LogoutConfirmation } from './LogoutConfirmation';

import { Button, Separator } from '../../ui';

type UserData = {
  name: string;
  socialName?: string;
  document: string;
  phone: string;
};

type UserProfileProps = {
  userData: UserData;
  onLogout: () => void;
  onEditProfile: () => void;
  onMyTickets: () => void;
};

export const UserProfile = ({
  userData,
  onLogout,
  onEditProfile,
  onMyTickets,
}: UserProfileProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const maskDocument = (document: string) => {
    const cleanDocument = document.replace(/\D/g, '');
    const lastTwo = cleanDocument.slice(-2);
    return `***.***.***-${lastTwo}`;
  };

  const maskPhone = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const lastFour = cleanPhone.slice(-4);
    return `(**) ****-${lastFour}`;
  };

  const handleLogoutClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirmLogout = () => {
    setShowConfirmation(false);
    onLogout();
  };

  const handleCancelLogout = () => {
    setShowConfirmation(false);
  };

  return (
    <AnimatePresence mode="wait">
      {showConfirmation ? (
        <motion.div
          key="confirmation"
          data-cy="user-profile-logout-confirmation"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
        >
          <LogoutConfirmation
            onConfirm={handleConfirmLogout}
            onCancel={handleCancelLogout}
          />
        </motion.div>
      ) : (
        <motion.div
          key="profile"
          data-cy="user-profile"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="flex flex-col"
        >
          <div className="bg-foreground/10 rounded-lg p-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="w-full flex flex-row justify-between"
            >
              <div className="flex flex-1 min-w-0 items-center gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.3, ease: 'easeOut' }}
                  className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center"
                >
                  <Icon.UserAccount width={24} height={24} className="text-primary" />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.3 }}
                  className="flex-1 min-w-0"
                >
                  <p
                    data-cy="user-profile-name"
                    className="font-medium text-foreground truncate"
                  >
                    {userData.socialName || userData.name}
                  </p>
                  <p className="text-sm text-muted-foreground">Usuário logado</p>
                </motion.div>
              </div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, duration: 0.3, ease: 'easeOut' }}
                className="w-10 h-10 bg-error/20 rounded-full flex items-center justify-center"
              >
                <Button
                  data-cy="user-profile-logout-button"
                  onClick={handleLogoutClick}
                  variant="ghost"
                  className="hover:bg-error/10 hover:text-error"
                >
                  <Icon.Logout width={20} height={20} className="text-error" />
                </Button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <Separator className="bg-foreground/10 my-4" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.3 }}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-row justify-between">
                <div>
                  <label className="text-sm font-medium text-foreground">CPF</label>
                  <p className="text-sm text-muted-foreground font-mono">
                    {maskDocument(userData.document)}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground">Telefone</label>
                  <p className="text-sm text-muted-foreground font-mono">
                    {maskPhone(userData.phone)}
                  </p>
                </div>
              </div>

              <Button
                data-cy="user-profile-my-tickets"
                variant="outlined"
                size="large"
                className="w-full"
                onClick={onMyTickets}
              >
                {ticketLabels.myTickets}
              </Button>
              <Button
                data-cy="user-profile-edit-button"
                variant="outlined"
                size="large"
                className="w-full"
                onClick={onEditProfile}
              >
                Editar Perfil
              </Button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
